import type { ComputedRef, Ref } from 'vue';
import { computed } from 'vue';

import { useSessionStorage } from 'shared/src/composables/storage/useSessionStorage';

import type { AuthModel } from '../models/authorizationState.model';
import type { Profile } from '../services/Authorization.contracts';

export class AuthorizationModuleStore {
  private authorizationState: Ref<AuthModel | any> = useSessionStorage('authorizationState', {});

  changeUserLoginStatus(isLogged: boolean): void {
    this.authorizationState.value = { ...this.authorizationState.value as AuthModel, isLogged };
  }

  setUserAuth(authObj: AuthModel | any): void {
    this.authorizationState.value = { ...this.authorizationState.value, ...authObj };
  }

  clearAuthorizationState(): void {
    sessionStorage.removeItem('sessionId');
    localStorage.removeItem('sessionId');
    localStorage.removeItem('logout');
    sessionStorage.removeItem('AppointmentApiToken');
    sessionStorage.removeItem('eWtId');
    sessionStorage.setItem('logoutInProgress', 'true');
    this.authorizationState.value = {};
  }

  get isUserLogged(): boolean {
    return !!this.authorizationState.value?.isLogged;
  }

  get UserCurrentAuthorizationState(): AuthModel | null {
    return this.authorizationState.value;
  }

  get UserAccessToken(): string | undefined {
    return this.authorizationState.value?.access_token;
  }

  get UserAccessTokenComputedRef(): ComputedRef<string | undefined> {
    return computed(() => {
      return this.authorizationState.value?.access_token;
    });
  }

  public get UserIdToken(): string | undefined {
    return this.authorizationState.value?.id_token;
  }

  public get UserProfile(): Profile | undefined {
    return this.authorizationState.value?.profile;
  }

  public get UserProfileAuthClaims(): Profile | undefined {
    return this.authorizationState.value?.authProfileClaims;
  }

  public get State(): Record<string, any> | undefined {
    return this.authorizationState.value?.state;
  }

  public get ExpiresAt(): number | undefined {
    return this.authorizationState.value?.expires_at;
  }

  public get getAuthMethod(): string | undefined {
    return this.authorizationState.value?.profile?.idp;
  }

  public get isOrganization(): boolean {
    return !!this.authorizationState.value?.profile?.orgid;
  }

  public get isDelegation(): boolean {
    return !!this.authorizationState.value?.profile?.delegation_id;
  }

  public get delegationId(): string | undefined {
    return this.authorizationState.value?.profile?.delegation_id;
  }

  public get authErrorState(): string | undefined {
    return this.authorizationState.value?.authErrorState;
  }

  public get userSid(): string | undefined {
    return this.authorizationState?.value?.profile?.sid;
  }

  public get userMid(): string | undefined {
    return this.authorizationState?.value?.profile?.mid;
  }
}

export default new AuthorizationModuleStore;